import { Injectable } from '@angular/core'
import { concat, forkJoin, from, Observable, of } from 'rxjs'
import { delay, map, mergeMap, switchMap, tap, toArray } from 'rxjs/operators'
import { CrudAction, CrudSubject } from 'src/utils/crud-subject'
import { CacheEntry } from '../../../utils/cache-entry'
import { ApiService } from '../../services/api.service'
import { AwsSourceResourceDto, SyncSourceDto, SyncSourceIssueDto, SyncSourceIssueScopeDto } from '../../services/dtos'
import { Arn } from '../../../utils/types'
import { ObservableCache } from 'src/utils/rxjs-utils'

export class SyncSource {
  static RESOURCE_POLL_INTERVAL = 5000

  static publishSubject$ = new CrudSubject<SyncSource>()
  // cache the source resources on a sync source.
  private resourceCache: Map<Arn, AwsSourceResourceDto> = null
  private issueCache: Array<SyncSourceIssueDto> = null
  // we retain a timestamp to know if the resources/issues that we've cached represent
  // the latest that the server knows about.  We store in this timestamp the time of the
  // most recently completed refresh.  If max(lastRefreshSuccess, lastRefreshFailure) is
  // ever greater than this timestamp, we know that the cached resources need to be
  // updated.
  private cacheTimestamp: Date = null

  private containedResourcesCache: AwsSourceResourceDto[] = null
  private containedResourcesTimestamp: Date = null

  constructor(
    private apiService: ApiService,
    public accountId: string,
    // tslint:disable-next-line:variable-name
    private _syncSourceDto: SyncSourceDto
  ) {}

  static load$(apiService: ApiService, accountId: string, syncSourceDto: SyncSourceDto): Observable<SyncSource> {
    const newSyncSource = new SyncSource(apiService, accountId, syncSourceDto)
    return newSyncSource.load$()
  }

  static loadSyncSource$(apiService: ApiService, accountId: string, endpointUri: string) {
    return apiService
      .getSyncSource(accountId, endpointUri)
      .pipe(switchMap((syncSourceDto) => SyncSource.load$(apiService, accountId, syncSourceDto)))
  }

  get endpointUri() {
    return this._syncSourceDto.endpointUri
  }

  get awsAccountId() {
    return this._syncSourceDto.awsAccountId
  }

  get region() {
    return this._syncSourceDto.region
  }

  private get _scanStatus() {
    return this._syncSourceDto.scanStatus
  }

  get lastScanStart() {
    return this._scanStatus.lastStart
  }

  get lastScanSuccess() {
    return this._scanStatus.lastSuccess
  }

  get lastScanFailure() {
    return this._scanStatus.lastFailure
  }

  private get lastScanComplete() {
    if (!this.lastScanFailure && !this.lastScanSuccess) {
      // refresh has never completed
      return null
    }

    if (!this.lastScanFailure) {
      // refresh has never failed
      return this.lastScanSuccess
    }

    if (!this.lastScanSuccess) {
      // refresh has never succeeded
      return this.lastScanFailure
    }

    if (this.lastScanFailure > this.lastScanSuccess) {
      // refresh has most recently failed
      return this.lastScanFailure
    }

    // through process of elimination, refresh has most recently succeeded
    return this.lastScanSuccess
  }

  get currentlyScanning() {
    return this.lastScanStart > this.lastScanComplete
  }

  get rootResources() {
    if (this.resourceCache) {
      return this.resourceCache.values()
    } else {
      return []
    }
  }

  get containedResources(): AwsSourceResourceDto[] {
    return this.containedResourcesCache || []
  }

  get resources(): AwsSourceResourceDto[] {
    return [...this.rootResources, ...this.containedResources]
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Issue Accessors
  ////////////////////////////////////////////////////////////////////////////////
  get refreshIssueDtos() {
    let scopes = [
      SyncSourceIssueScopeDto.REFRESH,
      SyncSourceIssueScopeDto.ENHANCE_FOR_RECOVERY_POINT,
      SyncSourceIssueScopeDto.SCAN,
    ]
    return this.issueCache && this.issueCache.filter((issue) => scopes.includes(issue.scope))
  }
  private _buildRefreshObservable$() {
    /**
     * Creates an observable that will refresh the SyncSource from the server, and then loads
     * resources and issues from the server if the resources and issues previously loaded
     * are out-of-date (or haven't been loaded)
     */
    return this.apiService.getSyncSource(this.accountId, this.endpointUri).pipe(
      map((syncSourceDto) => {
        // update internal DTO and return a SyncSource object
        this._syncSourceDto = syncSourceDto
        return this as SyncSource
      }),
      switchMap((syncSource) => {
        // fork parallel updates of resources and issues if they are out-of-date

        // default to returning cached resources
        let resources$ = of(this.resources)
        let issues$ = of(this.issueCache)

        // if the cached resources are null or out-of-date, update them instead
        if (
          syncSource.currentlyScanning ||
          syncSource.lastScanStart > syncSource.cacheTimestamp ||
          syncSource.resourceCache === null ||
          !syncSource.issueCache === null
        ) {
          resources$ = this.apiService.listSourceResources(this.accountId, this.endpointUri)
          issues$ = this.apiService.listSyncSourceIssues(this.accountId, this.endpointUri)
        }

        // launch these updates in parallel
        return forkJoin([of(syncSource), resources$, issues$])
      }),
      map(([syncSource, resources, issues]) => {
        // update cached resources and return the SyncSource object, now fully-populated.
        syncSource.resourceCache = new Map(resources.map((res) => [res.arn as Arn, res]))
        syncSource.issueCache = issues
        syncSource.cacheTimestamp = syncSource.lastScanComplete
        return syncSource
      }),
      switchMap((syncSource) => {
        let syncSource$ = of(syncSource)

        const containerArns = syncSource.resources
          .filter((res) => res.isContainer && res.descendantCount > 0)
          .map((res) => res.arn)

        if (
          containerArns.length > 0 &&
          (syncSource.containedResources === null || syncSource.lastScanStart > syncSource.containedResourcesTimestamp)
        ) {
          return from(containerArns).pipe(
            mergeMap((containerArn) =>
              this.apiService.listSourceResources(this.accountId, this.endpointUri, containerArn)
            ),
            toArray(),
            map((containedResources) => {
              syncSource.containedResourcesCache = Array.prototype.concat.apply([], containedResources)
              syncSource.containedResourcesTimestamp = syncSource.lastScanComplete
              return syncSource
            })
          )
        } else {
          return syncSource$
        }
      })
    )
  }

  load$() {
    // Populate this sync source, but don't poll if it's currently processing.  This is called from the context where
    // we're initially loading all sync sources, and we don't necessarily need to be refreshing this one.
    return this._buildRefreshObservable$().pipe(
      tap((syncSource) => SyncSource.publishSubject$.upsert(syncSource, syncSource.endpointUri))
    )
  }

  refreshSyncSource$(refreshStartTime: Date = null) {
    /**
     * Builds an observable that refreshes the SyncSource from the server, and then
     * refreshes resources and issues if they are out-of-date.  It continues to poll
     * and emit until any in-progress refresh operations complete.
     */

    return new Observable<SyncSource>((subscriber) => {
      let timeout = null
      let subscription = null
      const that = this

      function refresh() {
        timeout = null
        subscription = that._buildRefreshObservable$().subscribe(
          (syncSource) => {
            subscriber.next(syncSource)
            SyncSource.publishSubject$.upsert(syncSource, syncSource.endpointUri)

            const refreshStart = refreshStartTime || syncSource.lastScanStart
            if (refreshStart > syncSource.lastScanComplete) {
              // schedule another refresh in the future
              timeout = window.setTimeout(refresh, SyncSource.RESOURCE_POLL_INTERVAL)
              subscription = null
            } else {
              subscriber.complete()
              subscription = null
            }
          },
          (error) => {
            subscriber.error(error)
          }
        )
      }

      // invoke the initial refresh
      refresh()

      // return a subscription
      return {
        unsubscribe: () => {
          if (!!subscription) {
            subscription.unsubscribe()
          }
          if (timeout) {
            window.clearTimeout(timeout)
          }
        },
      }
    })
  }

  rescanSourceResources$(additionalIntegrationIds: Set<string>, identityProviderId: string = null) {
    return this.apiService
      .initiateSyncSourceScan(this.accountId, this.endpointUri, additionalIntegrationIds, identityProviderId)
      .pipe(
        delay(SyncSource.RESOURCE_POLL_INTERVAL),
        switchMap((scanSyncSourceDto) => this.refreshSyncSource$(scanSyncSourceDto.now))
      )
  }

  deleteSyncSourceIssue$(issueId: string, scope: SyncSourceIssueScopeDto) {
    return this.apiService.deleteSyncSourceIssue(this.accountId, this.endpointUri, scope.toString(), issueId).pipe(
      map((response) => {
        this.issueCache = this.issueCache.filter((issue) => issue.issueId !== issueId)
        SyncSource.publishSubject$.upsert(this, this.endpointUri)
        return response
      })
    )
  }
}

@Injectable({
  providedIn: 'root',
})
export class SyncSourceProviderService {
  private syncSourceCache: Map<string, CacheEntry<SyncSource>> = null
  private accountId: string = null

  constructor(private apiService: ApiService) {
    SyncSource.publishSubject$.subscribe((crudEvent) => {
      if (
        crudEvent.action === CrudAction.UPSERT &&
        crudEvent.entity.accountId === this.accountId &&
        !!this.syncSourceCache
      ) {
        this.syncSourceCache.set(crudEvent.entityId, new CacheEntry(crudEvent.entity))
      } else if (!!this.syncSourceCache) {
        this.syncSourceCache.delete(crudEvent.entityId)
      }
    })
  }
  private observableCache = new ObservableCache<SyncSource>()

  loadSyncSource$(accountId: string, endpointUri: string): Observable<SyncSource> {
    let observable: Observable<SyncSource>

    if (!!this.syncSourceCache && this.syncSourceCache.has(endpointUri)) {
      const syncSource = this.syncSourceCache.get(endpointUri)
      if (syncSource.entity.accountId !== accountId) {
        throw new Error('The sync source cache contains sync sources from the wrong account ID.')
      }
      observable = concat(of<SyncSource>(syncSource.entity), syncSource.entity.refreshSyncSource$())
    } else {
      observable = SyncSource.loadSyncSource$(this.apiService, accountId, endpointUri)
    }

    // We want to cache this observable so that multiple applications on the same sync source will share
    // a single stream of updates.
    return this.observableCache.cache(endpointUri, observable)
  }
}
